import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useUnitAdd } from "hooks/citajteo/dl_unit/useUnitAdd";
import { QUERY_KEYS } from "constant/react_query";
import toast from "react-hot-toast";

type DlUnitFormState = {
  slug: string;
  uType: string;
  num: string;
  fold: string;
  fValsText: string;
  scansText: string;
  pdfText: string;
};

const EMPTY_FORM: DlUnitFormState = {
  slug: "",
  uType: "",
  num: "",
  fold: "",
  fValsText: "{}",
  scansText: "[]",
  pdfText: "{}",
};

const DlUnitNew: React.FC = () => {
  const navigate = useNavigate();
  const { unitAddMutate } = useUnitAdd({
    invalidateKeys: [QUERY_KEYS.CITAJTEO_DL_UNIT_SEARCH],
  });
  const [form, setForm] = useState<DlUnitFormState>(EMPTY_FORM);
  const [errorText, setErrorText] = useState("");

  const handleChange =
    (field: keyof DlUnitFormState) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setForm((currentForm) => ({
        ...currentForm,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorText("");

    const trimmedSlug = form.slug.trim();
    if (trimmedSlug === "") {
      setErrorText("Slug ne sme biti prazan.");
      return;
    }

    const trimmedNum = form.num.trim();
    const parsedNum = trimmedNum === "" ? 0 : Number(trimmedNum);
    if (Number.isNaN(parsedNum)) {
      setErrorText("Broj mora biti numerička vrednost.");
      return;
    }

    let parsedFVals: Record<string, string> = {};
    let parsedScans: string[] = [];
    let parsedPdf: any = {};

    try {
      const rawFVals = form.fValsText.trim();
      parsedFVals = rawFVals === "" ? {} : JSON.parse(rawFVals);
    } catch (error) {
      setErrorText("fVals mora biti ispravan JSON objekat.");
      return;
    }

    if (
      typeof parsedFVals !== "object" ||
      parsedFVals === null ||
      Array.isArray(parsedFVals)
    ) {
      setErrorText("fVals mora biti JSON objekat.");
      return;
    }

    try {
      const rawScans = form.scansText.trim();
      parsedScans = rawScans === "" ? [] : JSON.parse(rawScans);
    } catch (error) {
      setErrorText("scans mora biti ispravan JSON niz.");
      return;
    }

    if (!Array.isArray(parsedScans) || !parsedScans.every((item) => typeof item === "string")) {
      setErrorText("scans mora biti JSON niz stringova.");
      return;
    }

    try {
      const rawPdf = form.pdfText.trim();
      parsedPdf = rawPdf === "" ? {} : JSON.parse(rawPdf);
    } catch (error) {
      setErrorText("pdf mora biti ispravan JSON objekat.");
      return;
    }

    unitAddMutate(
      {
        slug: trimmedSlug,
        uType: form.uType.trim(),
        num: parsedNum,
        fold: form.fold.trim(),
        fVals: parsedFVals,
        scans: parsedScans,
        pdf: parsedPdf,
      },
      {
        onSuccess: () => {
          toast.success("Uspešno dodavanje jedinice.");
          navigate("/admin-dashboard/dl-unit-li");
        },
        onError: (error) => {
          console.error("Error adding unit:", error);
          toast.error("Greška prilikom dodavanja jedinice.");
        },
      },
    );
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setErrorText("");
  };

  return (
    <FullWidth>
      <div className="App">
        <Form onSubmit={handleSubmit}>
          <Title>Nova jedinica</Title>

          <Field>
            <label htmlFor="slug">Slug</label>
            <Input id="slug" type="text" value={form.slug} onChange={handleChange("slug")} />
          </Field>

          <Field>
            <label htmlFor="uType">Tip</label>
            <Input id="uType" type="text" value={form.uType} onChange={handleChange("uType")} />
          </Field>

          <Field>
            <label htmlFor="num">Broj</label>
            <Input id="num" type="number" value={form.num} onChange={handleChange("num")} />
          </Field>

          <Field>
            <label htmlFor="fold">Fold</label>
            <Input id="fold" type="text" value={form.fold} onChange={handleChange("fold")} />
          </Field>

          <Field>
            <label htmlFor="fVals">fVals JSON</label>
            <TextArea id="fVals" rows={8} value={form.fValsText} onChange={handleChange("fValsText")} />
          </Field>

          <Field>
            <label htmlFor="scans">Scans JSON</label>
            <TextArea id="scans" rows={6} value={form.scansText} onChange={handleChange("scansText")} />
          </Field>

          <Field>
            <label htmlFor="pdf">PDF JSON</label>
            <TextArea id="pdf" rows={4} value={form.pdfText} onChange={handleChange("pdfText")} />
          </Field>

          {errorText !== "" && <ErrorText>{errorText}</ErrorText>}

          <Actions>
            <Button type="submit">OK</Button>
            <Button type="button" onClick={handleReset}>
              Reset
            </Button>
          </Actions>
        </Form>
      </div>
    </FullWidth>
  );
};

export default DlUnitNew;

const FullWidth = styled.div`
  width: 100%;
`;

const Form = styled.form`
  width: 100%;
  max-width: 900px;
`;

const Title = styled.h2`
  margin: 0 0 1rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.35rem;
    font-weight: 600;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.65rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.65rem;
  resize: vertical;
`;

const ErrorText = styled.div`
  margin-bottom: 1rem;
  color: #b00020;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button`
  padding: 0.4rem 0.9rem;
  cursor: pointer;
`;
